import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';

// Password strength calculator
function calculatePasswordStrength(password: string): { score: number; label: string; color: string } {
  let score = 0;
  
  if (password.length >= 8) score += 1;
  if (password.length >= 12) score += 1;
  if (/[a-z]/.test(password)) score += 1;
  if (/[A-Z]/.test(password)) score += 1;
  if (/[0-9]/.test(password)) score += 1;
  if (/[^a-zA-Z0-9]/.test(password)) score += 1;

  if (score <= 2) return { score: 1, label: '약함', color: 'bg-red-500' };
  if (score <= 4) return { score: 2, label: '보통', color: 'bg-yellow-500' };
  return { score: 3, label: '강함', color: 'bg-green-500' };
}

export default function ResetPassword() {
  const { updatePassword } = useAuth();
  const navigate = useNavigate();
  
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const [touched, setTouched] = useState<{ password?: boolean; passwordConfirm?: boolean }>({});
  const [fieldErrors, setFieldErrors] = useState<{ password?: string; passwordConfirm?: string }>({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // Validation functions
  const validatePassword = useCallback((value: string): string | undefined => {
    if (!value) return '새 비밀번호를 입력해주세요';
    if (value.length < 8) return '비밀번호는 8자 이상이어야 합니다';
    if (!/[a-zA-Z]/.test(value)) return '영문자를 포함해주세요';
    if (!/[0-9]/.test(value)) return '숫자를 포함해주세요';
    return undefined;
  }, []);

  const validatePasswordConfirm = useCallback((value: string, pwd: string): string | undefined => {
    if (!value) return '비밀번호 확인을 입력해주세요';
    if (value !== pwd) return '비밀번호가 일치하지 않습니다';
    return undefined;
  }, []);

  // Real-time validation
  useEffect(() => {
    const errors: { password?: string; passwordConfirm?: string } = {};
    if (touched.password) errors.password = validatePassword(password);
    if (touched.passwordConfirm) errors.passwordConfirm = validatePasswordConfirm(passwordConfirm, password);
    setFieldErrors(errors);
  }, [password, passwordConfirm, touched, validatePassword, validatePasswordConfirm]);

  // Password strength
  const passwordStrength = useMemo(() => {
    if (!password) return null;
    return calculatePasswordStrength(password);
  }, [password]);

  // Password match indicator
  const passwordsMatch = useMemo(() => {
    if (!passwordConfirm) return null;
    return password === passwordConfirm;
  }, [password, passwordConfirm]);

  // Form validity
  const isFormValid = useMemo(() => {
    return (
      password &&
      passwordConfirm &&
      !validatePassword(password) &&
      !validatePasswordConfirm(passwordConfirm, password)
    );
  }, [password, passwordConfirm, validatePassword, validatePasswordConfirm]);

  const handleBlur = (field: 'password' | 'passwordConfirm') => {
    setTouched(prev => ({ ...prev, [field]: true }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Touch all fields
    setTouched({ password: true, passwordConfirm: true });

    // Validate
    const pwdError = validatePassword(password);
    const confirmError = validatePasswordConfirm(passwordConfirm, password);

    if (pwdError || confirmError) {
      setFieldErrors({ password: pwdError, passwordConfirm: confirmError });
      return;
    }

    setIsLoading(true);

    try {
      await updatePassword(password);
      navigate('/login', { 
        state: { message: '비밀번호가 변경되었습니다. 새 비밀번호로 로그인해주세요.' }
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : '비밀번호 변경에 실패했습니다';
      if (message.includes('expired') || message.includes('invalid')) {
        setError('비밀번호 재설정 링크가 만료되었거나 유효하지 않습니다. 다시 요청해주세요.');
      } else {
        setError(message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center py-12 px-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">새 비밀번호 설정</h1>
          <p className="text-gray-700 dark:text-gray-300">새로운 비밀번호를 입력해주세요</p>
        </div>

        <div className="card">
          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            {error && (
              <div 
                className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-600 dark:text-red-400 text-sm"
                role="alert"
                aria-live="polite"
              >
                {error}
              </div>
            )}

            {/* Password */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                새 비밀번호
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onBlur={() => handleBlur('password')}
                  className={`input pr-12 ${touched.password && fieldErrors.password ? 'border-red-500 focus:ring-red-500' : ''}`}
                  placeholder="영문, 숫자 포함 8자 이상"
                  autoComplete="new-password"
                  aria-describedby="password-requirements password-error"
                  aria-invalid={touched.password && !!fieldErrors.password}
                  required
                  minLength={8}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 focus:outline-none focus:ring-2 focus:ring-space-blue rounded p-1"
                  aria-label={showPassword ? '비밀번호 숨기기' : '비밀번호 표시'}
                >
                  {showPassword ? (
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
              {/* Password Strength Indicator */}
              {passwordStrength && (
                <div className="mt-2">
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-1.5 bg-gray-200 dark:bg-zinc-700 rounded-full overflow-hidden">
                      <div 
                        className={`h-full transition-all duration-300 ${passwordStrength.color}`}
                        style={{ width: `${(passwordStrength.score / 3) * 100}%` }}
                        role="progressbar"
                        aria-valuenow={passwordStrength.score}
                        aria-valuemin={0}
                        aria-valuemax={3}
                        aria-label="비밀번호 강도"
                      />
                    </div>
                    <span className={`text-xs font-medium ${
                      passwordStrength.score === 1 ? 'text-red-500' :
                      passwordStrength.score === 2 ? 'text-yellow-600 dark:text-yellow-500' :
                      'text-green-500'
                    }`}>
                      {passwordStrength.label}
                    </span>
                  </div>
                  <p id="password-requirements" className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    영문, 숫자, 특수문자를 조합하면 더 안전합니다
                  </p>
                </div>
              )}
              {touched.password && fieldErrors.password && (
                <p id="password-error" className="mt-1 text-sm text-red-500" role="alert">
                  {fieldErrors.password}
                </p>
              )}
            </div>

            {/* Password Confirm */}
            <div>
              <label htmlFor="passwordConfirm" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                비밀번호 확인
              </label>
              <div className="relative">
                <input
                  id="passwordConfirm"
                  type={showPasswordConfirm ? 'text' : 'password'}
                  value={passwordConfirm}
                  onChange={(e) => setPasswordConfirm(e.target.value)}
                  onBlur={() => handleBlur('passwordConfirm')}
                  className={`input pr-12 ${touched.passwordConfirm && fieldErrors.passwordConfirm ? 'border-red-500 focus:ring-red-500' : ''}`}
                  placeholder="비밀번호 재입력"
                  autoComplete="new-password"
                  aria-describedby={fieldErrors.passwordConfirm ? 'passwordConfirm-error' : 'passwordConfirm-hint'}
                  aria-invalid={touched.passwordConfirm && !!fieldErrors.passwordConfirm}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPasswordConfirm(!showPasswordConfirm)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 focus:outline-none focus:ring-2 focus:ring-space-blue rounded p-1"
                  aria-label={showPasswordConfirm ? '비밀번호 숨기기' : '비밀번호 표시'}
                >
                  {showPasswordConfirm ? (
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
              {/* Password match indicator */}
              {passwordsMatch !== null && (
                <p 
                  id="passwordConfirm-hint"
                  className={`mt-1 text-sm flex items-center gap-1 ${passwordsMatch ? 'text-green-500' : 'text-red-500'}`}
                >
                  {passwordsMatch ? (
                    <>
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      비밀번호가 일치합니다
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                      비밀번호가 일치하지 않습니다
                    </>
                  )}
                </p>
              )}
              {touched.passwordConfirm && fieldErrors.passwordConfirm && !passwordsMatch && (
                <p id="passwordConfirm-error" className="mt-1 text-sm text-red-500" role="alert">
                  {fieldErrors.passwordConfirm}
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={isLoading || !isFormValid}
              className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-space-blue focus:ring-offset-2"
              aria-busy={isLoading}
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" aria-hidden="true">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  변경 중...
                </span>
              ) : '비밀번호 변경'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
